/**
 * Upload Queue Manager
 * 
 * Manages client-side upload queue with rate limiting, retry logic, and progress tracking
 */

export interface UploadItem {
  file: File;
  imageId?: string; // For original uploads
  retries: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  error?: string;
}

export interface UploadProgress {
  total: number;
  completed: number;
  failed: number;
  current?: string; // Current file being uploaded
}

export type UploadCallback = (progress: UploadProgress) => void;
export type UploadFunction = (item: UploadItem) => Promise<any>;

export interface QueueOptions {
  batchSize?: number; // Files per batch (default: 5)
  delayBetweenBatches?: number; // Milliseconds (default: 3000)
  maxRetries?: number; // Per file (default: 3)
  retryDelays?: number[]; // Exponential backoff delays in ms (default: [1000, 2000, 4000, 8000])
}

const DEFAULT_OPTIONS: Required<QueueOptions> = {
  batchSize: 5,
  delayBetweenBatches: 3000,
  maxRetries: 3,
  retryDelays: [1000, 2000, 4000, 8000],
};

export class UploadQueue {
  private queue: UploadItem[] = [];
  private isProcessing = false;
  private progress: UploadProgress = {
    total: 0,
    completed: 0,
    failed: 0,
  };
  private options: Required<QueueOptions>;
  private onProgress?: UploadCallback;
  private uploadFn?: UploadFunction;

  constructor(options: QueueOptions = {}, onProgress?: UploadCallback) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.onProgress = onProgress;
  }

  /**
   * Set the upload function
   */
  setUploadFunction(fn: UploadFunction) {
    this.uploadFn = fn;
  }

  /**
   * Add items to the queue
   */
  addItems(items: UploadItem[]) {
    this.queue.push(...items);
    this.progress.total += items.length;
    this.updateProgress();
    
    if (!this.isProcessing) {
      this.process();
    }
  }

  /**
   * Add a single item to the queue
   */
  addItem(item: UploadItem) {
    this.addItems([item]);
  }

  /**
   * Process the queue
   */
  private async process() {
    if (this.isProcessing || !this.uploadFn) {
      return;
    }

    this.isProcessing = true;

    while (this.queue.length > 0) {
      // Get next batch
      const batch = this.queue.splice(0, this.options.batchSize);
      
      // Process batch sequentially
      for (const item of batch) {
        if (item.status === 'completed') {
          continue;
        }

        item.status = 'uploading';
        this.progress.current = item.file.name;
        this.updateProgress();

        try {
          await this.uploadFn(item);
          item.status = 'completed';
          this.progress.completed++;
          this.updateProgress();
        } catch (error) {
          item.retries++;
          
          if (item.retries >= this.options.maxRetries) {
            item.status = 'failed';
            item.error = error instanceof Error ? error.message : 'Unknown error';
            this.progress.failed++;
            this.updateProgress();
          } else {
            // Retry with exponential backoff
            const delay = this.options.retryDelays[Math.min(item.retries - 1, this.options.retryDelays.length - 1)];
            item.status = 'pending';
            await new Promise(resolve => setTimeout(resolve, delay));
            // Re-add to queue for retry
            this.queue.unshift(item);
          }
        }
      }

      // Delay between batches (except for the last batch)
      if (this.queue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, this.options.delayBetweenBatches));
      }
    }

    this.isProcessing = false;
    this.progress.current = undefined;
    this.updateProgress();
  }

  /**
   * Update progress and notify callback
   */
  private updateProgress() {
    if (this.onProgress) {
      // Create a new object to ensure reactivity
      const progressUpdate: UploadProgress = {
        total: this.progress.total,
        completed: this.progress.completed,
        failed: this.progress.failed,
        current: this.progress.current,
      };
      this.onProgress(progressUpdate);
    }
  }

  /**
   * Get current progress
   */
  getProgress(): UploadProgress {
    return { ...this.progress };
  }

  /**
   * Clear the queue
   */
  clear() {
    this.queue = [];
    this.progress = {
      total: 0,
      completed: 0,
      failed: 0,
    };
    this.updateProgress();
  }

  /**
   * Check if queue is processing
   */
  isQueueProcessing(): boolean {
    return this.isProcessing;
  }

  /**
   * Get queue length
   */
  getQueueLength(): number {
    return this.queue.length;
  }
}

